import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { AzureService } from 'src/app/core/auth/azure.service'
import { User } from 'src/app/shared/models/user.model'
import { FormsModule } from '@angular/forms'
import { DropdownModule } from 'primeng/dropdown'
import { MultiSelectModule } from 'primeng/multiselect'
import { SliderModule } from 'primeng/slider'
import { CheckboxModule } from 'primeng/checkbox'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputNumberModule } from 'primeng/inputnumber'
import { ButtonModule } from 'primeng/button'
import mappingGroups from './MappingGroups.json'
import { MappingGroup } from 'src/app/core/auth/azure.service'
import { SettingsService } from './services/settings.service'
import { MessageService } from 'primeng/api'

export interface SettingsRule extends Rule {
  isEditing?: boolean
}

export interface Rule {
  ruleId?: string // Optional until received from backend
  capType: string
  thresholdPct: number
  capPct: number | null
  capPctRange: [number | null, number | null] | []
  customers: any[]
  priceRange: [number | null, number | null]
  priority: number
  active: boolean
  updatedAt?: number // Optional until received from backend
}

@Component({
  selector: 'app-settings',
  template: `
    <div class="submitButtons">
      <p-button
        [outlined]="true"
        (click)="addRule()"
        label="Add Rule"
        icon="pi pi-plus" />
      <p-button
        *ngIf="rules.length > 0"
        (click)="submitRules()"
        label="Submit Rules"
        icon="pi pi-check" />
    </div>

    <div
      *ngFor="let rule of rules.slice().reverse(); let i = index"
      class="rulesContainer">
      <h4>Rule {{ rules.length - i }}</h4>
      <div class="ruleContainer">
        <div class="ruleContainer__inputs">
          <div class="ruleContainer__inputs--section1">
            <div class="inputGroup">
              <label class="text-caption-strong-bold">Cap Type</label>
              <p-dropdown
                class="inputs"
                [options]="['hard', 'soft']"
                [(ngModel)]="rule.capType"
                placeholder="Select a Business Area"
                [disabled]="!rule.isEditing" />
            </div>
            <div class="inputGroup">
              <label class="text-caption-strong-bold"
                >Threshold Percentage</label
              >
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.thresholdPct"
                placeholder="Threshold Percentage"
                [disabled]="!rule.isEditing" />
            </div>

            <div class="inputGroup">
              <label class="text-caption-strong-bold">Customers</label>
              <p-multiSelect
                class="inputs"
                [options]="customers"
                [(ngModel)]="rule.customers"
                [disabled]="!rule.isEditing"
                placeholder="Select Customers" />
            </div>
            <div class="inputGroup">
              <label class="text-caption-strong-bold">Priority</label>
              <span class="text-small-roman"
                >(lower = higher priority, or vice versa)</span
              >
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.priority"
                placeholder="Priority"
                [disabled]="!rule.isEditing" />
            </div>
          </div>
          <div class="ruleContainer__inputs--section2">
            <div class="inputGroup">
              <label class="text-caption-strong-bold">Cap Percentage</label>
              <p-inputNumber
                class="inputs"
                type="number"
                [(ngModel)]="rule.capPct"
                placeholder="Cap Percentage"
                [disabled]="!rule.isEditing || rule.capType !== 'hard'" />
            </div>
            <div class="inputGroup">
              <label class="text-caption-strong-bold"
                >Cap Percentage Range</label
              >
              <p-slider
                [(ngModel)]="rule.capPctRange"
                [range]="true"
                [disabled]="!rule.isEditing || rule.capType !== 'soft'"
                styleClass="w-14rem" />
              <div class="inputGroup__slider">
                <span>{{ rule.capPctRange[0] }}</span>
                <span>{{ rule.capPctRange[1] }}</span>
              </div>
            </div>
            <div class="inputGroup">
              <label class="text-caption-strong-bold">Price Range</label>
              <p-slider
                [(ngModel)]="rule.priceRange"
                [range]="true"
                max="1000"
                [disabled]="!rule.isEditing || rule.capType !== 'soft'"
                styleClass="w-14rem" />
              <div class="inputGroup__slider">
                <span>{{ rule.priceRange[0] }}</span>
                <span>{{ rule.priceRange[1] }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="ruleContainer__buttons">
          <div class="ruleContainer__buttons--section1">
            <p-button
              (click)="toggleEdit(rule)"
              [icon]="rule.isEditing ? 'pi pi-check' : 'pi pi-pencil'"
              [label]="rule.isEditing ? 'Save' : 'Edit'"
              size="small">
            </p-button>
            <p-button
              (click)="removeRule(i)"
              label="Remove"
              icon="pi pi-times"
              size="small" />
          </div>
          <div class="ruleContainer__buttons--section2">
            <div class="inputGroup">
              <p-checkbox
                [(ngModel)]="rule.active"
                [binary]="true"
                label="Active"
                (onChange)="submitRules()" />
            </div>
          </div>
        </div>
      </div>
      <hr />
    </div>
  `,
  styles: [
    `
      .inputGroup {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 15%;

        &__checkbox {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        &__slider {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }
      }

      .inputs {
        display: contents;
      }

      .rulesContainer {
        display: flex;
        flex-direction: column;
        padding-inline: 1rem;
      }

      .ruleContainer {
        display: flex;
        gap: 15px;
        flex-direction: row;
        padding-inline: 1rem;

        &__inputs {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          gap: 25px;
          &--section1,
          &--section2 {
            display: flex;
            flex-direction: row;
            gap: 5%;
          }
        }

        &__buttons {
          display: flex;
          flex-direction: column;
          gap: 25px;
          justify-content: center;

          &--section1 {
            display: flex;
            gap: 15px;
          }

          &--section2 {
            display: flex;
            padding-inline: 30%;
          }
        }
      }

      .submitButtons {
        display: flex;
        gap: 15px;
        padding: 1rem;
        margin-bottom: 1rem;
        justify-content: space-between;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    SliderModule,
    CheckboxModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    ButtonModule,
  ],
})
export class Settings implements OnInit {
  private azureService = inject(AzureService)
  private settingsService = inject(SettingsService)
  private messageService = inject(MessageService)

  private readonly _destroying$ = new Subject<void>()

  currentUser: User | null = null
  userGroups: { label: string; value: string }[] = []
  selectedBusinessArea: string = ''
  mappingGroups: MappingGroup[] = mappingGroups
  rules: SettingsRule[] = []
  customers: { label: string; value: string }[] = []

  constructor() {
    this.azureService
      .getCurrentUser()
      .pipe(takeUntil(this._destroying$))
      .subscribe((userData) => {
        this.currentUser = userData
      })

      this.azureService.selectedBusinessArea.subscribe((businessArea) => {
        this.selectedBusinessArea = businessArea;
        this.getRules();
      });
  
    this.loadCustomers()
  }

  ngOnInit(): void {
    this.getRules()
  }

  getRules() {
    this.settingsService.getRules(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Rules:', response)
        this.rules = response.rules
      },
      error: (error) => {
        console.error('Error getting rules:', error)
      },
    })
  }


  loadCustomers(): void {
    this.settingsService.getCustomers(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Customers:', response)
        this.customers = response.customers.map(
          (customer: string, idx: number) => ({
            label: customer,
            value: idx.toString(),
          })
        )
      },
      error: (error) => {
        console.error('Error getting customers:', error)
      },
    })
  }

  /*  validateCapPctRange(rule: Rule): void {
    if (rule.capPctRange[0] !== null && rule.capPctRange[1] !== null) {
      if (rule.capPctRange[0] > rule.capPctRange[1]) {
        // If the minimum is greater than the maximum, reset the maximum
        rule.capPctRange[1] = rule.capPctRange[0]
      }
    }
  } */

  addRule(): void {
    const newRule: SettingsRule = {
      capType: 'soft',
      thresholdPct: 15,
      capPct: null,
      capPctRange: [10, 15],
      customers: [],
      priceRange: [null, null],
      priority: 1,
      active: true,
      isEditing: true,
    }
    this.rules.push(newRule)
  }

  removeRule(index: number): void {
    this.rules.splice(this.rules.length - index - 1, 1)
    this.submitRules('remove')
  }

  toggleEdit(rule: SettingsRule): void {
    if (rule.isEditing) {
      console.log('Saving rule:', rule)
    }
    rule.isEditing = !rule.isEditing
  }

  submitRules(action?: string): void {
    this.rules.forEach((rule) => {
      rule.isEditing = false // Disable editing state
    })

    if (!this.validateRules()) return
    const submittedRules = this.rules.map(({ isEditing, ...rule }) => rule)
    this.settingsService
      .createRules(this.selectedBusinessArea, submittedRules)
      .subscribe({
        next: (response) => {
          console.log('Response:', response)
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: action === 'remove' ? 'Rule removed!' : 'Rules changed!',
          })
          this.rules = response.rules
        },
        error: (error) => {
          console.error('Error submitting rules:', error)
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'There is some error while processing your request.',
          })
        },
      })

    console.log('Submitting Rules:', this.rules)
  }

  validateRules(): boolean {
    if (this.rules.length === 0) return true
    return this.rules.every((rule) => {
      if (rule.capType === 'hard') {
        if (rule.capPct === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage is required for hard cap type.',
          })
          return false
        } else {
          rule.priceRange = [null, null]
          rule.capPctRange = []
        }
      } else {
        if (rule.capPctRange[0] === null || rule.capPctRange[1] === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage Range is required for soft cap type.',
          })
          return false
        }
        rule.capPct = null
      }
      return true
    })
  }
}
